/**
 * YJS ↔ MARKDOWN BRIDGE
 * 
 * ELIMINATES THE CONVERSION NIGHTMARE:
 * OLD: YJS → ProseMirror → HTML → cleanChangeAttributes → sanitizeForJSON → Claude API
 * NEW: YJS → Markdown (direct, semantic, lossless)
 * 
 * This is the GAME CHANGER that fixes your core serialization pain
 */

import * as Y from 'yjs';
import { createFileLogger } from '../../utils/logger';
import { SafeYJSExtraction } from './yjsTextExtraction';


export interface YJSMarkdownOptions {
  preserveAttributes?: boolean;
  includeMetadata?: boolean;
  handleSuggestions?: 'include' | 'exclude' | 'mark';
  maxDepth?: number;
}

const DEFAULT_OPTIONS: YJSMarkdownOptions = {
  preserveAttributes: false,
  includeMetadata: false, 
  handleSuggestions: 'include', // ✅ CONSISTENT: Claude sees suggestions as accepted content (same as backend for AI operations)
  maxDepth: 50
};

/**
 * THE UNIFIED YJS ↔ MARKDOWN BRIDGE
 * 
 * Direct conversion that preserves semantic meaning without intermediate HTML
 */
export class YJSMarkdownBridge {
  
  /**
   * YJS → MARKDOWN: Convert Y.js document directly to clean markdown
   * 
   * NO HTML INTERMEDIATE STEP = NO SANITIZATION HELL
   */
  static toMarkdown(ydoc: any, options: YJSMarkdownOptions = {}): string {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    
    // YJS markdown conversion started (verbose logs removed)
    
    try {
      // 🚨 CRITICAL FIX: Use SafeYJSExtraction to eliminate duplicate nodes from suggestion mode
      // This is essential to prevent Claude from seeing both old and new nodes
      
      // Using SafeYJSExtraction with AI filtering (verbose logs removed)
      
      // Try to get schema from global context (set by DocsApp)
      let schema;
      try {
        // Schema should be available from the global context
        if ((globalThis as any).__DOCS_SCHEMA__) {
          schema = (globalThis as any).__DOCS_SCHEMA__;
          // Schema available from global context (verbose logs removed)
        } else if ((globalThis as any).__DOCS_SCHEMA_READY__) {
          console.error('[YJSMarkdownBridge] 🚨 Schema flag set but schema missing!');
          throw new Error('Schema flag set but schema object missing');
        } else {
          console.warn('[YJSMarkdownBridge] ⚠️ Schema not yet available (DocsApp not mounted)');
          throw new Error('Schema not available - DocsApp not mounted yet');
        }
      } catch (schemaError) {
        console.error('[YJSMarkdownBridge] 🚨 CRITICAL: Schema not available - this will cause duplicate nodes!', schemaError);
        
        // 🚨 DO NOT USE FALLBACK - it can reintroduce duplicate nodes
        // Instead, wait briefly for schema to become available or fail fast
        
        // Quick retry for schema availability (in case it's loading)
        // Note: Can't use await in non-async function, so do synchronous retry
        
        if ((globalThis as any).__DOCS_SCHEMA__) {
          console.log('[YJSMarkdownBridge] ✅ Schema became available after retry');
          schema = (globalThis as any).__DOCS_SCHEMA__;
          
          // Proceed with safe extraction
          const safeResult = SafeYJSExtraction.extractContent(ydoc, schema, {
            fragmentKey: 'prosemirror',
            filterForAI: opts.handleSuggestions === 'exclude' || opts.handleSuggestions === 'include',
            returnBoth: false
          });
          
          if (safeResult.success && safeResult.markdown) {
            return safeResult.markdown;
          }
        }
        
        // 🚨 SALVAGE STRATEGY: Use old method with CLEAR WARNING instead of failing completely
        console.error('[YJSMarkdownBridge] 🚨 CRITICAL: Using potentially contaminated fallback due to schema failure!');
        console.error('[YJSMarkdownBridge] 🚨 This may cause duplicate nodes - monitor for issues!');
        
        const yxml = ydoc.getXmlFragment('prosemirror');
        if (!yxml || yxml.length === 0) {
          console.log('[YJSMarkdownBridge] ⚠️ Empty or missing prosemirror fragment');
          return '';
        }
        
        // Use existing conversion method as fallback (with duplicate risk)
        const result = this.convertYXMLToMarkdown(yxml, opts, 0);
        
        // Add warning to result so calling code knows about the risk
        console.warn('[YJSMarkdownBridge] ⚠️ CONTAMINATION RISK: Result may contain duplicates due to schema failure');
        return result;
      }
      
      // Use SafeYJSExtraction with AI filtering to prevent duplicate nodes
      const safeResult = SafeYJSExtraction.extractContent(ydoc, schema, {
        fragmentKey: 'prosemirror',
        filterForAI: opts.handleSuggestions === 'exclude' || opts.handleSuggestions === 'include', // Always filter for AI
        returnBoth: false
      });
      
      if (safeResult.success) {
        return safeResult.markdown || '';
      } else {
        console.error('[YJSMarkdownBridge] ❌ Safe extraction failed, using fallback:', safeResult.error);
        
        const yxml = ydoc.getXmlFragment('prosemirror');
        if (!yxml || yxml.length === 0) {
          // ✅ NORMAL: Empty documents are common for previews/new docs - don't log as warning
          return '';
        }
        
        // Use existing conversion method as fallback
        const result = this.convertYXMLToMarkdown(yxml, opts, 0);
        return result;
      }
    } catch (error) {
      console.error('[YJSMarkdownBridge] 🚨 CRITICAL: Safe conversion failed:', error);
      return '';
    }
  }

  /**
   * MARKDOWN → YJS: Convert markdown directly back to Y.js structure
   */
  static fromMarkdown(markdown: string, ydoc: any, options: YJSMarkdownOptions = {}): void {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    
    try {
      const yxml = ydoc.getXmlFragment('prosemirror');
      const elements = this.parseMarkdownToYXML(markdown, opts);
      
      // Clear existing content
      ydoc.transact(() => {
        yxml.delete(0, yxml.length);
        if (elements.length > 0) {
          yxml.insert(0, elements);
        }
      });
    } catch (error) {
      const log = createFileLogger('YJS_MARKDOWN_BRIDGE');
      log.error('[YJSMarkdownBridge] CRITICAL ERROR converting Markdown to Y.js:', error);
      
      console.error('[YJSMarkdownBridge] 🚨 MARKDOWN→YJS CONVERSION FAILED:', {
        errorMessage: error instanceof Error ? error.message : String(error),
        markdownLength: markdown?.length || 'unknown',
        docGuid: ydoc?.guid,
        timestamp: new Date().toISOString()
      });
      
      // 🚨 CRITICAL: Don't silently fail - throw error to make failures visible
      throw new Error(`YJSMarkdownBridge markdown→YJS conversion failed: ${error instanceof Error ? error.message : 'Unknown error'}.`);
    }
  }

  /**
   * CORE CONVERSION: Y.js XML → Markdown (recursive)
   */
  private static convertYXMLToMarkdown(
    element: any, 
    options: YJSMarkdownOptions,
    depth: number
  ): string {
    if (depth > options.maxDepth!) {
      return '[MAX_DEPTH_EXCEEDED]';
    }

    let markdown = '';
    
    for (let i = 0; i < element.length; i++) {
      const child = element.get(i);
      
      if (!child) continue;

      if (child && typeof child.toString === 'function' && !child.nodeName) {
        // Pure text node - add directly
        markdown += this.processTextNode(child, options);
      } else if (child && (child.nodeName || child.getAttribute)) {
        // Element node - convert based on type
        markdown += this.processElementNode(child, options, depth + 1);
      }
    }

    return markdown;
  }

  // 🎯 UNICODE NATIVE: decodeHtmlEntities method removed
  // We no longer create HTML entities, so no decoding needed

  /**
   * Process Y.js text nodes with formatting using the public toDelta() API.
   *
   * toDelta() returns delta operations like:
   *   [{ insert: "hello " }, { insert: "world", attributes: { bold: true } }]
   *
   * This is the correct, stable way to read Y.XmlText — it avoids both private
   * internals (_content, .text) and toString() which emits XML wrappers like
   * <fontFamily>…</fontFamily> for styled runs.
   */
  private static processTextNode(textNode: any, _options: YJSMarkdownOptions): string {
    if (!textNode || typeof textNode.toDelta !== 'function') {
      console.warn('[YJSMarkdownBridge] ⚠️ Text node has no toDelta() method:', textNode);
      return '';
    }

    let result = '';
    try {
      const delta: Array<{ insert?: any; attributes?: Record<string, any> }> = textNode.toDelta();
      for (const op of delta) {
        if (typeof op.insert !== 'string' || !op.insert) continue;

        const attrs = op.attributes || {};
        // Ignore purely visual marks — fontFamily, fontSize, textColor, etc. have
        // no markdown equivalent and must not produce output tokens.
        const isBold = !!(attrs.bold || attrs.strong);
        const isItalic = !!(attrs.italic || attrs.em);
        const isCode = !!attrs.code;
        const isStrike = !!(attrs.strike || attrs.strikethrough);
        const isLink = !!attrs.link;

        let segment = op.insert;

        if (isCode) {
          segment = `\`${segment}\``;
        } else {
          if (isBold && isItalic) {
            segment = `***${segment}***`;
          } else if (isBold) {
            segment = `**${segment}**`;
          } else if (isItalic) {
            segment = `*${segment}*`;
          }
          if (isStrike) segment = `~~${segment}~~`;
          if (isLink) {
            const href = typeof attrs.link === 'object' ? (attrs.link.href || '') : String(attrs.link);
            if (href) {
              segment = `[${segment}](${href})`;
            }
            // no href → keep segment as plain text
          }
        }

        result += segment;
      }
    } catch (error) {
      console.error('[YJSMarkdownBridge] Error reading text node via toDelta():', error);
      return '';
    }

    return result;
  }

  /**
   * Process Y.js element nodes based on their semantic type
   */
  private static processElementNode(
    element: any, 
    options: YJSMarkdownOptions, 
    depth: number
  ): string {
    // Get element type from Y.js attributes or node name
    const elementType = element.getAttribute('type') || element.nodeName || 'paragraph';
    const changeStatus = element.getAttribute('change_status');
    
    // ✅ CRITICAL DEBUG: Check element for contamination WITHOUT using toString()
    const elementId = element.getAttribute('id');
    
    // 🚨 DO NOT USE element.toString() - it causes fontFamily contamination!
    // Instead, check element attributes and content directly
    let hasContamination = false;
    let contaminationSource = '';
    
    try {
      // Check element attributes for fontFamily contamination
      if (element.getAttribute && element.getAttribute('fontFamily')) {
        hasContamination = true;
        contaminationSource = 'element_attribute';
      }
      
      // Check if element has fontFamily in its raw structure (avoid toString)
      const anyElement = element as any;
      if (anyElement._content || anyElement.content) {
        const contentStr = JSON.stringify(anyElement._content || anyElement.content);
        if (contentStr.includes('fontFamily')) {
          hasContamination = true;
          contaminationSource = 'element_content';
        }
      }
    } catch (error) {
      // Ignore errors in contamination detection
    }
    
    if (hasContamination) {
      console.log('[YJSMarkdownBridge] 🚨 DETECTED FONTFAMILY CONTAMINATION (without toString):', {
        elementType,
        elementId,
        contaminationSource,
        warning: 'Element contains fontFamily data that must be cleaned'
      });
    }
    
    // Handle suggestion mode filtering
    if (options.handleSuggestions === 'exclude' && changeStatus === 'old') {
      return ''; // Skip old content in suggestions
    }
    
    // ✅ CRITICAL FIX: For 'include' mode, show suggestions as accepted content
    // But EXCLUDE old content that was replaced by suggestions
    if (options.handleSuggestions === 'include' && changeStatus === 'old') {
      console.log('[YJSMarkdownBridge] 🚫 SKIPPING OLD CONTENT:', {
        elementType,
        elementId,
        changeStatus,
        reason: 'old_content_replaced_by_suggestions'
        // ❌ REMOVED: textPreview using toString() - causes contamination
      });
      return ''; // Skip old content when including suggestions - show only the "accepted" suggestions
    }
    
    // ✅ ADDITIONAL FIX: Also skip content marked as deleted or replaced
    if (changeStatus === 'deleted' || changeStatus === 'replaced') {
      console.log('[YJSMarkdownBridge] 🚫 SKIPPING DELETED/REPLACED CONTENT:', {
        elementType,
        elementId,
        changeStatus,
        reason: 'content_marked_for_removal'
        // ❌ REMOVED: textPreview using toString() - causes contamination
      });
      return '';
    }
    
    // ✅ CRITICAL DEBUG: Log what content we ARE including
    if (changeStatus !== 'old' && changeStatus !== 'deleted' && changeStatus !== 'replaced') {
      console.log('[YJSMarkdownBridge] ✅ INCLUDING CONTENT:', {
        elementType,
        elementId,
        changeStatus: changeStatus || 'normal',
        reason: 'active_content'
        // ❌ REMOVED: textPreview using toString() - causes contamination
      });
    }

    // Get text content recursively
    let content = this.convertYXMLToMarkdown(element, options, depth);
    
    // ✅ CRITICAL FIX: Strip ALL HTML-like tags from content and convert to proper markdown
    // This handles cases where Y.js elements contain embedded HTML-like formatting
    if (content.includes('<') || content.includes('</')) {
      console.log('[YJSMarkdownBridge] 🔧 STRIPPING HTML TAGS from content:', {
        elementType,
        elementId,
        beforeStripping: content.substring(0, 200),
        containsHtmlTags: content.includes('<')
      });
      
      // ✅ ENHANCED: Convert HTML formatting to proper markdown FIRST
      content = content
        .replace(/<strong[^>]*>(.*?)<\/strong>/g, '**$1**')
        .replace(/<em[^>]*>(.*?)<\/em>/g, '*$1*')
        .replace(/<code[^>]*>(.*?)<\/code>/g, '`$1`')
        .replace(/<u[^>]*>(.*?)<\/u>/g, '$1'); // Remove underline
      
      // Remove fontFamily and other styling tags (with any attributes)
      content = content.replace(/<fontFamily[^>]*>/g, '');
      content = content.replace(/<\/fontFamily>/g, '');
      content = content.replace(/<fontSize[^>]*>/g, '');
      content = content.replace(/<\/fontSize>/g, '');
      content = content.replace(/<textColor[^>]*>/g, '');
      content = content.replace(/<\/textColor>/g, '');
      content = content.replace(/<backgroundColor[^>]*>/g, '');
      content = content.replace(/<\/backgroundColor>/g, '');
      
      // Remove any other remaining HTML tags
      content = content.replace(/<[^>]+>/g, '');
      
      console.log('[YJSMarkdownBridge] ✅ CONVERTED HTML TO MARKDOWN:', {
        elementType,
        elementId,
        afterConversion: content.substring(0, 200),
        stillContainsHtml: content.includes('<')
      });
    }
    
    // ✅ CRITICAL DEBUG: Check if content still contains fontFamily after stripping
    if (content.includes('fontFamily') || content.includes('<fontFamily')) {
      console.log('[YJSMarkdownBridge] 🚨 CONTENT STILL CONTAINS FONTFAMILY AFTER STRIPPING:', {
        elementType,
        elementId,
        contentPreview: content.substring(0, 300),
        containsFontFamilyTag: content.includes('<fontFamily'),
        containsFontFamilyAttr: content.includes('fontFamily=')
      });
    }
    
    if (!content.trim()) return '';

    // Convert to appropriate markdown based on semantic type
    switch (elementType.toLowerCase()) {
      case 'heading':
        const level = element.getAttribute('level') || 1;
        const headingPrefix = '#'.repeat(Math.min(level, 6));
        return `${headingPrefix} ${content.trim()}\n\n`;
      
      case 'paragraph':
      case 'p':
        // Handle suggestion marking
        if (options.handleSuggestions === 'mark' && changeStatus === 'new') {
          return `> **[SUGGESTED]** ${content.trim()}\n\n`;
        }
        return `${content.trim()}\n\n`;
      
      case 'bullet_list':
      case 'ul':
        return this.processList(element, options, depth, 'bullet');
      
      case 'ordered_list':
      case 'ol':
        return this.processList(element, options, depth, 'ordered');
      
      case 'list_item':
      case 'li':
        return `- ${content.trim()}\n`;
      
      case 'code_block':
      case 'pre':
        const language = element.getAttribute('language') || '';
        return `\`\`\`${language}\n${content.trim()}\n\`\`\`\n\n`;
      
      case 'blockquote':
        return content.split('\n').map(line => `> ${line}`).join('\n') + '\n\n';
      
      case 'table':
        return this.processTable(element, options, depth);
      
      case 'table_row':
        return this.processTableRow(element, options, depth);
      
      case 'table_cell':
      case 'table_header':
        return content.trim().replace(/\|/g, '\\|'); // Escape pipes in table cells
      
      // Handle ProseMirror marks that appear as XML elements
      case 'strong':
        return `**${content.trim()}**`;
      
      case 'em':
        return `*${content.trim()}*`;
      
      case 'code':
        return `\`${content.trim()}\``;
      
      case 'underline':
        // Markdown doesn't have native underline, preserve text
        return content.trim();
      
      case 'strike':
      case 'strikethrough':
        return `~~${content.trim()}~~`;
      
      case 'superscript':
        return `^${content.trim()}`;
      
      case 'subscript':
        return `~${content.trim()}`;
      
      case 'textcolor':
      case 'backgroundcolor':
      case 'fontsize':
      case 'fontfamily':
      case 'fontweight':
        // These visual marks don't translate to markdown - just preserve content
        return content.trim();
      
      case 'link':
        const href = element.getAttribute('href');
        if (href) {
          return `[${content.trim()}](${href})`;
        }
        return content.trim();
      
      case 'highlight':
        // Some markdown flavors support highlighting
        return `==${content.trim()}==`;
      
      case 'math_inline':
        // Inline mathematical formula - convert to $...$
        const inlineLatex = element.getAttribute('latex') || '';
        console.log('[YJSMarkdownBridge] Converting inline math to markdown:', {
          latex: inlineLatex.substring(0, 50),
          elementId
        });
        return `$${inlineLatex}$`;
      
      case 'math_display':
        // Display mathematical formula - convert to $$...$$
        const displayLatex = element.getAttribute('latex') || '';
        console.log('[YJSMarkdownBridge] Converting display math to markdown:', {
          latex: displayLatex.substring(0, 50),
          elementId
        });
        return `$$${displayLatex}$$\n\n`;
      
      default:
        // Fallback: treat as paragraph
        return `${content.trim()}\n\n`;
    }
  }

  /**
   * Process list elements with proper nesting - Enhanced for complex structures
   */
  private static processList(
    listElement: any,
    options: YJSMarkdownOptions,
    depth: number,
    type: 'bullet' | 'ordered'
  ): string {
    let markdown = '';
    let counter = 1;

    // ✅ NEW: Enhanced list processing to preserve complex nested structures
    console.log('[YJSMarkdownBridge] 🔧 Processing complex list:', {
      listType: type,
      depth,
      itemCount: listElement.length,
      listAttributes: listElement.getAttribute ? {
        order: listElement.getAttribute('order'),
        style_id: listElement.getAttribute('style_id'),
        id: listElement.getAttribute('id')
      } : {}
    });

    for (let i = 0; i < listElement.length; i++) {
      const item = listElement.get(i);
      if (item && item.getAttribute && 
          (item.getAttribute('type') === 'list_item' || item.nodeName === 'li')) {
        
        // ✅ ENHANCED: Process list item with nested structure awareness
        const itemContent = this.convertYXMLToMarkdown(item, options, depth + 1);
        const prefix = type === 'ordered' ? `${counter}. ` : '- ';
        
        // Check if this list item has nested content (like nested lists)
        const hasNestedLists = item.length > 0 && Array.from({length: item.length}, (_, idx) => item.get(idx))
          .some((child: any) => child?.getAttribute?.('type') === 'bullet_list' || 
                               child?.getAttribute?.('type') === 'ordered_list' ||
                               child?.nodeName === 'ul' || child?.nodeName === 'ol');
        
        console.log('[YJSMarkdownBridge] 🔧 Processing list item:', {
          itemIndex: i,
          hasNestedLists,
          itemType: item.getAttribute?.('type'),
          childCount: item.length,
          contentPreview: itemContent.substring(0, 100)
        });
        
        // ✅ FIX: Preserve multi-line content instead of flattening with trim()
        // Split content into lines and properly indent continuation lines
        const contentLines = itemContent.split('\n').filter(line => line.trim());
        
        if (contentLines.length === 0) {
          markdown += `${prefix}\n`;
        } else if (contentLines.length === 1 && !hasNestedLists) {
          // Single line - use original behavior for simple cases (no nesting)
          markdown += `${prefix}${contentLines[0].trim()}\n`;
        } else {
          // Multi-line content - preserve structure with proper indentation
          markdown += `${prefix}${contentLines[0].trim()}\n`;
          
          // Indent continuation lines to align with list content
          const indentSpaces = prefix.replace(/./g, ' '); // Replace each char with space
          for (let j = 1; j < contentLines.length; j++) {
            const line = contentLines[j].trim();
            if (line) {
              markdown += `${indentSpaces}${line}\n`;
            }
          }
        }
        
        counter++;
      }
    }

    return markdown + '\n';
  }

  /**
   * Process table elements
   */
  private static processTable(
    tableElement: any,
    options: YJSMarkdownOptions,
    depth: number
  ): string {
    let markdown = '';
    let headerProcessed = false;

    for (let i = 0; i < tableElement.length; i++) {
      const row = tableElement.get(i);
      if (row && row.getAttribute && 
          (row.getAttribute('type') === 'table_row' || row.nodeName === 'tr')) {
        
        const rowMarkdown = this.processTableRow(row, options, depth + 1);
        markdown += rowMarkdown;
        
        // Add separator after header row
        if (!headerProcessed) {
          const cellCount = this.countTableCells(row);
          markdown += '|' + ' --- |'.repeat(cellCount) + '\n';
          headerProcessed = true;
        }
      }
    }

    return markdown + '\n';
  }

  /**
   * Process table row elements
   */
  private static processTableRow(
    rowElement: any,
    options: YJSMarkdownOptions,
    depth: number
  ): string {
    const cells: string[] = [];

    for (let i = 0; i < rowElement.length; i++) {
      const cell = rowElement.get(i);
      if (cell && cell.getAttribute && 
          (cell.getAttribute('type')?.includes('table_') || ['td', 'th'].includes(cell.nodeName || ''))) {
        
        const cellContent = this.convertYXMLToMarkdown(cell, options, depth + 1);
        cells.push(cellContent.trim().replace(/\|/g, '\\|'));
      }
    }

    return '| ' + cells.join(' | ') + ' |\n';
  }

  /**
   * Count table cells in a row
   */
  private static countTableCells(rowElement: any): number {
    let count = 0;
    for (let i = 0; i < rowElement.length; i++) {
      const cell = rowElement.get(i);
      if (cell && cell.getAttribute && 
          (cell.getAttribute('type')?.includes('table_') || ['td', 'th'].includes(cell.nodeName || ''))) {
        count++;
      }
    }
    return count;
  }

  /**
   * MARKDOWN → YJS: Parse markdown and create Y.js elements
   * Enhanced to handle HTML-like content that should be markdown
   */
  private static parseMarkdownToYXML(markdown: string, options: YJSMarkdownOptions): any[] {
    const elements: any[] = [];
    
    // ✅ ENHANCED: Pre-process to convert HTML-like markup to proper markdown
    
    // 🔍 QUOTE TRACE: Check for quotes before HTML preprocessing
    const hasQuotesBeforeHtmlClean = /["']/.test(markdown);
    if (hasQuotesBeforeHtmlClean) {
      console.log('🔍 [YJS_HTML_CLEAN_TRACE] Markdown contains quotes before HTML preprocessing:', {
        markdownPreview: markdown.substring(0, 150),
        quotesFound: markdown.match(/["']/g) || [],
        hasHtmlTags: /<[^>]+>/.test(markdown)
      });
    }
    
    const cleanedMarkdown = markdown
      .replace(/<strong>(.*?)<\/strong>/g, '**$1**')
      .replace(/<em>(.*?)<\/em>/g, '*$1*')
      .replace(/<code>(.*?)<\/code>/g, '`$1`')
      .replace(/<u>(.*?)<\/u>/g, '$1') // Remove underline tags
      .replace(/<[^>]+>/g, ''); // Remove any other HTML tags
      
    // 🔍 QUOTE TRACE: Check if quotes survived HTML preprocessing
    if (hasQuotesBeforeHtmlClean) {
      console.log('🔍 [YJS_HTML_CLEAN_TRACE] After HTML preprocessing:', {
        cleanedPreview: cleanedMarkdown.substring(0, 150),
        quotesRemaining: (cleanedMarkdown.match(/["']/g) || []).length,
        quotesPreserved: (cleanedMarkdown.match(/["']/g) || []).length > 0,
        htmlTagsRemoved: /<[^>]+>/.test(markdown) && !/<[^>]+>/.test(cleanedMarkdown)
      });
    }
    
    console.log('[YJSMarkdownBridge] 🔧 Pre-processed markdown for parsing:', {
      originalLength: markdown.length,
      cleanedLength: cleanedMarkdown.length,
      hadHtmlTags: markdown.includes('<'),
      preview: cleanedMarkdown.substring(0, 200)
    });
    
    const lines = cleanedMarkdown.split('\n');
    let i = 0;

    while (i < lines.length) {
      const line = lines[i].trim();
      
      if (!line) {
        i++;
        continue;
      }

      // Headers
      const headerMatch = line.match(/^(#{1,6})\s+(.*)$/);
      if (headerMatch) {
        const level = headerMatch[1].length;
        const headingEl = new Y.XmlElement('heading');
        headingEl.setAttribute('level', String(level));
        headingEl.insert(0, [this.createYText(headerMatch[2])]);
        elements.push(headingEl);
        i++;
        continue;
      }

      // Code blocks
      if (line.startsWith('```')) {
        const language = line.substring(3).trim();
        i++;
        const codeLines: string[] = [];
        while (i < lines.length && !lines[i].trim().startsWith('```')) {
          codeLines.push(lines[i]);
          i++;
        }
        i++; // consume closing ```
        const codeEl = new Y.XmlElement('code_block');
        if (language) codeEl.setAttribute('language', language);
        const codeText = new Y.XmlText();
        codeText.insert(0, codeLines.join('\n'));
        codeEl.insert(0, [codeText]);
        elements.push(codeEl);
        continue;
      }

      // Lists
      const listMatch = line.match(/^(\s*)([-*+]|\d+\.)\s+(.*)$/);
      if (listMatch) {
        const listType = /^\d+\./.test(listMatch[2]) ? 'ordered_list' : 'bullet_list';
        const { listElement, nextIndex } = this.parseListItems(lines, i, listType, listMatch[1].length);
        elements.push(listElement);
        i = nextIndex;
        continue;
      }

      // Regular paragraph — collect consecutive non-special lines
      let paragraphText = line;
      i++;
      while (i < lines.length && lines[i].trim() && !this.isSpecialLine(lines[i])) {
        paragraphText += ' ' + lines[i].trim();
        i++;
      }

      elements.push(this.createYParagraph(paragraphText));
    }

    return elements;
  }

  /**
   * Parse a run of list items at the given indent level into a real Y.XmlElement list.
   * Recurses for nested sub-lists (greater indent).
   */
  private static parseListItems(
    lines: string[],
    startIndex: number,
    listType: string,
    baseIndent: number
  ): { listElement: Y.XmlElement; nextIndex: number } {
    const listElement = new Y.XmlElement(listType);

    let i = startIndex;
    while (i < lines.length) {
      const line = lines[i];
      const listMatch = line.match(/^(\s*)([-*+]|\d+\.)\s+(.*)$/);

      if (!listMatch) {
        // Empty line — allow one blank line within a list, then stop
        if (!line.trim()) { i++; continue; }
        break; // Non-list, non-blank line ends the list
      }

      const indent = listMatch[1].length;
      const content = listMatch[3];

      if (indent < baseIndent) break; // Parent-level item — stop

      if (indent > baseIndent) {
        // Nested list belonging to the last list_item
        const nestedType = /^\d+\./.test(listMatch[2]) ? 'ordered_list' : 'bullet_list';
        const { listElement: nestedList, nextIndex } = this.parseListItems(lines, i, nestedType, indent);
        // Attach nested list to last list_item's paragraph's parent (the list_item)
        const lastItem = listElement.get(listElement.length - 1) as Y.XmlElement | undefined;
        if (lastItem) {
          lastItem.insert(lastItem.length, [nestedList]);
        }
        i = nextIndex;
        continue;
      }

      // Same indent — new sibling list item
      const listItem = new Y.XmlElement('list_item');
      const para = this.createYParagraph(content);
      listItem.insert(0, [para]);
      listElement.insert(listElement.length, [listItem]);
      i++;
    }

    return { listElement, nextIndex: i };
  }

  /**
   * Create a real Y.XmlElement('paragraph') containing a Y.XmlText with inline
   * markdown formatting applied via toDelta-compatible format() calls.
   */
  private static createYParagraph(text: string): Y.XmlElement {
    const para = new Y.XmlElement('paragraph');
    const yText = this.createYText(text);
    para.insert(0, [yText]);
    return para;
  }

  /**
   * Create a Y.XmlText node with inline markdown formatting marks applied.
   * Handles **bold**, *italic*, ***bold+italic***, `code`, ~~strikethrough~~, [link](url).
   */
  private static createYText(text: string): Y.XmlText {
    const yText = new Y.XmlText();

    if (!text.includes('*') && !text.includes('`') && !text.includes('~') && !text.includes('[')) {
      yText.insert(0, text, {});
      return yText;
    }

    const pattern = /(\*\*\*[^\n]+?\*\*\*|\*\*[^\n]+?\*\*|\*[^*\n]+?\*|`[^`\n]+`|~~[^\n]+?~~|\[[^\]\n]+\]\([^)\n]+\))/g;
    let lastIndex = 0;
    let currentPos = 0;

    let match;
    while ((match = pattern.exec(text)) !== null) {
      if (match.index > lastIndex) {
        const plain = text.substring(lastIndex, match.index);
        // Pass {} explicitly to prevent Y.js sticky attribute inheritance
        yText.insert(currentPos, plain, {});
        currentPos += plain.length;
      }

      const m = match[0];
      let segment: string;
      let marks: Record<string, any> = {};

      if (m.startsWith('***') && m.endsWith('***')) {
        segment = m.slice(3, -3);
        marks = { bold: true, italic: true };
      } else if (m.startsWith('**') && m.endsWith('**')) {
        segment = m.slice(2, -2);
        marks = { bold: true };
      } else if (m.startsWith('*') && m.endsWith('*')) {
        segment = m.slice(1, -1);
        marks = { italic: true };
      } else if (m.startsWith('`') && m.endsWith('`')) {
        segment = m.slice(1, -1);
        marks = { code: true };
      } else if (m.startsWith('~~') && m.endsWith('~~')) {
        segment = m.slice(2, -2);
        marks = { strikethrough: true };
      } else if (m.startsWith('[')) {
        const lm = m.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (lm) {
          segment = lm[1];
          marks = { link: { href: lm[2], target: '_blank' } };
        } else {
          segment = m;
        }
      } else {
        segment = m;
      }

      yText.insert(currentPos, segment!, marks);
      currentPos += segment!.length;
      lastIndex = match.index + m.length;
    }

    if (lastIndex < text.length) {
      const remaining = text.substring(lastIndex);
      yText.insert(currentPos, remaining, {});
    }

    return yText;
  }

  /**
   * Returns true if the line starts a block-level markdown construct.
   */
  private static isSpecialLine(line: string): boolean {
    const trimmed = line.trim();
    return /^(#{1,6}\s|```|[-*+]\s|\d+\.\s|>\s)/.test(trimmed);
  }

  /**
   * UTILITY: Extract plain text from Y.js document for Claude API
   */
  static extractPlainText(ydoc: any, maxLength?: number): string {
    const markdown = this.toMarkdown(ydoc, { 
      preserveAttributes: false,
      handleSuggestions: 'include' // ✅ CRITICAL FIX: Include suggestions for Claude's plain text extraction
    });
    
    // Convert markdown to plain text (remove formatting)
    let plainText = markdown
      .replace(/^#{1,6}\s+/gm, '')     // Remove headers
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.*?)\*/g, '$1')      // Remove italic
      .replace(/`(.*?)`/g, '$1')       // Remove code
      .replace(/^\s*[-*+]\s+/gm, '')   // Remove list markers
      .replace(/^\s*\d+\.\s+/gm, '')   // Remove numbered lists
      .replace(/^\s*>\s+/gm, '')       // Remove blockquotes
      .replace(/\n\s*\n/g, '\n')       // Normalize whitespace
      .trim();

    if (maxLength && plainText.length > maxLength) {
      plainText = plainText.substring(0, maxLength) + '...';
    }

    return plainText;
  }

  /**
   * UTILITY: Get document statistics
   */
  static getDocumentStats(ydoc: any): {
    totalElements: number;
    wordCount: number;
    characterCount: number;
    hasStructure: boolean;
  } {
    const yxml = ydoc.getXmlFragment('prosemirror');
    const plainText = this.extractPlainText(ydoc);
    
    return {
      totalElements: yxml.length,
      wordCount: plainText.split(/\s+/).filter(word => word.length > 0).length,
      characterCount: plainText.length,
      hasStructure: yxml.length > 1
    };
  }

}

/**
 * CONVENIENCE EXPORTS for the workflow fix
 */
export const toMarkdown = YJSMarkdownBridge.toMarkdown;
export const fromMarkdown = YJSMarkdownBridge.fromMarkdown;
export const extractPlainText = YJSMarkdownBridge.extractPlainText;
export const getDocumentStats = YJSMarkdownBridge.getDocumentStats; 